import React from 'react';
import { Calendar } from 'lucide-react';
import { SectionCard, SectionFields, SectionFieldConfig, SectionFieldsProps } from './SectionCard';
import type { CarePlanCertificationPeriod } from '../../../../dtos/patientmr/careplans/careplan.dto';

const FIELDS: SectionFieldConfig[] = [
  { key: 'certificationPeriodFrom', label: 'From', kind: 'date', required: true },
  { key: 'certificationPeriodTo', label: 'To', kind: 'date', required: true },
];

export const FROM_CERT_PERIOD_NOTE = 'From the certification period';

/** True when the plan record carries a certification period with at least one date. */
export function hasFixedCertPeriod(period: CarePlanCertificationPeriod | null | undefined): period is CarePlanCertificationPeriod {
  return !!period && (!!period.startDate || !!period.endDate);
}

/**
 * Phase 19 F26: with a period on the plan record, its dates replace the plan's
 * `certificationPeriodFrom` / `certificationPeriodTo` (the keys the backend reads).
 * Returns `data` unchanged when there is no period or the keys already match.
 */
export function applyCertPeriod<T extends object>(data: T, period: CarePlanCertificationPeriod | null | undefined): T {
  if (!hasFixedCertPeriod(period)) return data;
  const from = period.startDate || '';
  const to = period.endDate || '';
  const current = data as { certificationPeriodFrom?: unknown; certificationPeriodTo?: unknown };
  if (current.certificationPeriodFrom === from && current.certificationPeriodTo === to) return data;
  return { ...data, certificationPeriodFrom: from, certificationPeriodTo: to };
}

interface CertPeriodSectionProps extends SectionFieldsProps {
  title?: string;
  /** `card`: its own collapsible card (OT); `inline`: a sub-heading inside the configuration block (RN, PT, ST) */
  variant?: 'card' | 'inline';
  collapsed?: boolean;
  onToggle?: () => void;
  /** Phase 19 F26: the plan's certification period; when set, the dates are shown read-only */
  fixedPeriod?: CarePlanCertificationPeriod | null;
}

/** Phase 19 F25: certification period (CMS-485 items 2-3), `certificationPeriodFrom` / `certificationPeriodTo`. */
const CertPeriodSection: React.FC<CertPeriodSectionProps> = ({
  title = 'Certification Period', variant = 'inline', collapsed = false, onToggle, fixedPeriod = null, ...fieldProps
}) => {
  const fields = hasFixedCertPeriod(fixedPeriod) ? (
    <>
      <SectionFields
        fields={FIELDS}
        values={{ certificationPeriodFrom: fixedPeriod.startDate || '', certificationPeriodTo: fixedPeriod.endDate || '' }}
        onChange={fieldProps.onChange}
        readOnly
      />
      <p className="text-xs text-gray-600 mt-2">{FROM_CERT_PERIOD_NOTE}</p>
    </>
  ) : <SectionFields fields={FIELDS} {...fieldProps} />;
  if (variant === 'card') {
    return <SectionCard title={title} icon={Calendar} tone="blue" collapsed={collapsed} onToggle={onToggle}>{fields}</SectionCard>;
  }
  return (
    <div className="mt-6">
      <h5 className="text-sm font-medium text-gray-700 mb-3">{title}</h5>
      {fields}
    </div>
  );
};

export default CertPeriodSection;
